import { Button, Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { Browser } from "@capacitor/browser";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import styles from "../../content/pages/homepage.module.css";
import { firstPageStories } from "../../mockup/firstPageStories";
import imagePageOne from "../../assets/imagePageOne.svg";
import { LinearProgress } from "@mui/material";
import { isPlatform } from "@ionic/react";
import { isMobileWebBrowser, mobile } from "src/utils/platformUtils";
import Logo from "src/components/LogoSign";
import BlackLogo from "./BlackLogo";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { trackSignupScreenSeenAt } from "src/utils/analyticsCapFirebase";

interface SecondGetStartedPageProps {
    handleBack: () => void;
    handleClose: () => void;
}

const SecondGetStartedPage = ({ handleBack, handleClose }: SecondGetStartedPageProps) => {
    // Auth0 logic
    const { loginWithRedirect, isAuthenticated } = useAuth0();
    //Import navigation
    const navigate = useNavigate();
    // State for the story index
    const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
    // State for the story animation
    const [showingNewStory, setShowingNewStory] = useState(true);
    // Prevent stacked story timeouts (fast taps) from flashing the wrong image
    const storyChangeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const iosOrAndroid = isPlatform("hybrid");
    const isMobileWeb = isMobileWebBrowser();
    const isMobile = iosOrAndroid || isMobileWeb;

    // Theme and media query for mobile/tablet
    const theme = useTheme();
    // const isMobileOrTablet = useMediaQuery(theme.breakpoints.between("xs", "lg"));
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("lg")) && ("ontouchstart" in window || navigator.maxTouchPoints > 0);
    // For tablets (between 600px and 1200px): require touch + exclude short landscape
    // so Mac/desktop and Nest Hub (1024x600) keep phone mock; real iPads keep imgTablet
    const isTabletWidth = useMediaQuery(theme.breakpoints.between("sm", "lg"));
    const isShortLandscape = useMediaQuery("(max-height: 700px) and (orientation: landscape)");
    const isTablet =
        isTabletWidth &&
        ("ontouchstart" in window || navigator.maxTouchPoints > 0) &&
        !isShortLandscape;

    // This variable will be true only for the last story
    const isLastStory = currentStoryIndex === firstPageStories.length - 1;

    useEffect(() => {
        // Preload story assets so remounted slides (key=index) paint from cache — no black gap
        firstPageStories.forEach((story) => {
            const phoneImg = new window.Image();
            phoneImg.src = story.img;
            const tabletImg = new window.Image();
            tabletImg.src = story.imgTablet;
        });
        return () => {
            if (storyChangeTimeoutRef.current) clearTimeout(storyChangeTimeoutRef.current);
        };
    }, []);

    // NOTE: This component is used inside a Drawer on HomePage,
    // so we intentionally avoid redirects / splash behaviors here.

    // Function to handle the login
    const login = async (event) => {
        // Stop the event propagation
        event.stopPropagation();
        await trackSignupScreenSeenAt("manual_click", "help_getting_started");
        // Login with redirect
        await loginWithRedirect({
            async openUrl(url) {
                // Redirect using Capacitor's Browser plugin
                await Browser.open({
                    url,
                    windowName: "_self",
                });
            },
        });
    };

    //Function to handle the next story
    const handleNextStory = () => {
        if (currentStoryIndex < firstPageStories.length - 1) {
            setShowingNewStory(false);
            if (storyChangeTimeoutRef.current) clearTimeout(storyChangeTimeoutRef.current);
            storyChangeTimeoutRef.current = setTimeout(() => {
                setCurrentStoryIndex((prev) => prev + 1);
                setShowingNewStory(true);
            }, 50);
        }
    };

    //Function to handle the previous story
    const handlePreviousStory = () => {
        if (currentStoryIndex > 0) {
            setShowingNewStory(false);
            if (storyChangeTimeoutRef.current) clearTimeout(storyChangeTimeoutRef.current);
            storyChangeTimeoutRef.current = setTimeout(() => {
                setCurrentStoryIndex((prev) => prev - 1);
                setShowingNewStory(true);
            }, 50);
        }
    };

    //Function to handle the click on the container
    const handleClick = (event) => {
        // If the click is on the mobile/tablet or the play/pause button or the login button, return
        // if (isMobileOrTablet || event.target.closest("#playPauseFab") || event.target.closest("button")) {
        //   return;
        // }
        // Get the clientX and windowWidth
        const { clientX } = event;
        const windowWidth = window.innerWidth;
        const margin = windowWidth * 0.5;

        // If the click is on the left side, go to the previous story
        if (clientX < margin) {
            handlePreviousStory();
        } else if (clientX > windowWidth - margin) {
            // If the click is on the right side, go to the next story
            handleNextStory();
        }
    };

    // Progress bar logic
    const getSubProgressWidth = (index) => (index < currentStoryIndex || index === currentStoryIndex ? 100 : 0);


    return (
        <>
            {/* Header */}
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1000,
                    backgroundColor: "#EDEDED",
                    borderBottom: `1px solid ${PureLightThemeCaaS.colors.alpha.white[10]}`,
                }}
            >
                {/* <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" p={0.5}>
                    <IconButton
                        aria-label="back"
                        onClick={handleBack}
                    // sx={{
                    //     visibility: activeView === "menu" ? "hidden" : "visible",
                    // }}
                    >
                        <ArrowBackIcon sx={{ color: PureLightThemeCaaS.colors.alpha.black[100] }} />
                    </IconButton>
                    <Typography variant="h4" fontSize={"16px"} color={PureLightThemeCaaS.colors.alpha.black[100]}>
                        Getting Started
                    </Typography>
                    <IconButton aria-label="close" onClick={handleClose} sx={{
                        visibility: "hidden",
                    }}>
                        <CloseIcon sx={{ color: PureLightThemeCaaS.colors.alpha.black[100] }} />
                    </IconButton>
                </Box> */}
            </Box>
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    // paddingBottom: "env(safe-area-inset-bottom)",
                }}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    sx={{
                        position: "relative",
                        mt: 0,
                        // pt: {
                        //   xs: iosOrAndroid ? 8 : 6,
                        //   sm: iosOrAndroid ? 6 : 1,
                        //   md: 1,
                        //   lg: 1,
                        //   "@media (max-height: 667px)": iosOrAndroid ? 2 : 0,
                        // },
                        justifyContent: "flex-start",
                        // Remove fixed height to allow natural content flow and prevent extra space
                        minHeight: 0,
                        //all the next properties are to prevent moving the page when the user is swiping the stories
                        // touchAction: isMobileWeb || !iosOrAndroid ? "auto" : "none",
                        // touchAction: "pan-y",
                        userSelect: isMobileWeb || !iosOrAndroid ? "auto" : "none",
                        WebkitTouchCallout: isMobileWeb || !iosOrAndroid ? "auto" : "none",
                        "& *": {
                            //   touchAction: isMobileWeb || !iosOrAndroid ? "auto" : "none",
                            // touchAction: "pan-y",
                            userSelect: isMobileWeb || !iosOrAndroid ? "auto" : "none",
                            pointerEvents: "auto",
                        },
                    }}
                    alignItems="center"
                    bgcolor="#EDEDED"
                    overflow="hidden"
                    onClick={handleClick}
                    //   onTouchEnd={handleClick}
                    // Events for mobile/tablet touch
                    // onTouchStart={isMobileOrTablet ? onTouchStart : null}
                    // onTouchMove={isMobileOrTablet ? onTouchMove : null}
                    // onTouchEnd={isMobileOrTablet ? onTouchEnd : null}
                    // Class name for the container for the width of the app
                    className={styles.boxContainerApp}
                >
                    {/* Progress bars */}
                    <Box
                        display="flex"
                        width="100%"
                        // p={1}
                        pt={0.9}
                        pb={1}
                        pr={0.5}
                        pl={0.5}
                        sx={{
                            // Keep in normal flow to avoid overlapping the title
                            flexShrink: 0,
                            //   pt: {
                            //     xs: iosOrAndroid ? 4 : 3, // more padding in mobile/tablet
                            //     sm: iosOrAndroid ? 2 : 3,
                            //     md: 3,
                            //     lg: 3,
                            //     "@media (max-height: 667px)": iosOrAndroid ? 0 : 3,
                            //   },
                        }}
                    >
                        {firstPageStories.map((story, index) => (
                            <Box key={index} flex={1} mx={0.1} >
                                <LinearProgress
                                    variant="determinate"
                                    color="inherit"
                                    value={getSubProgressWidth(index)}
                                    sx={{
                                        height: "2px",
                                        color: `${PureLightThemeCaaS.colors.alpha.orange[100]}`,
                                        bgcolor: `${PureLightThemeCaaS.colors.alpha.gray[100]}`,
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>

                    {/* Story content */}
                    <Box
                        sx={{
                            // NOTE: Avoid flex growth here to prevent extra blank space
                            // between the story image and the RotoGPT logo section below.
                            width: "100%",
                            pt: {
                                xs: 1,
                                "@media (min-height: 667px)": {
                                    // iPhone SE height
                                    pt: 1,
                                },
                                sm: 2,
                                lg: 2,
                            },
                            // flex: {
                            //   xs: 0.8,
                            //   // "@media (max-height: 667px)": {
                            //   //   flex: "auto",
                            //   // },
                            // },
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: {
                                xs: 1,
                                "@media (max-height: 667px)": {
                                    gap: 2,
                                },
                            },
                            minHeight: 0,
                        }}
                    >
                        <Typography
                            variant="h4"
                            color={`${PureLightThemeCaaS.colors.alpha.black[100]}`}
                            textAlign="center"
                            className={`${styles.tradeTeam} ${showingNewStory ? styles.storyFadeIn : ""}`}
                            onAnimationEnd={() => setShowingNewStory(false)}
                            sx={{
                                fontSize: "18px",
                                "@media (max-height: 667px)": {
                                    fontSize: "16px",
                                },
                            }}
                        >
                            {firstPageStories[currentStoryIndex].title}
                        </Typography>

                        <Typography
                            variant="body1"
                            color={`${PureLightThemeCaaS.colors.alpha.black[100]}`}
                            textAlign="center"
                            className={showingNewStory ? styles.storyFadeIn : ""}
                            sx={{
                                width: { xs: "85%", lg: "70%" },
                                height: "60px",
                                fontSize: "16px",
                                "@media (max-height: 667px)": {
                                    fontSize: "14px",
                                },
                            }}
                        >
                            {firstPageStories[currentStoryIndex].content}
                        </Typography>

                        {/* Story image + side nav arrows (fixed to container edges; hide at ends; black on light bg) */}
                        <Box
                            sx={{
                                position: "relative",
                                width: "100%",
                                alignSelf: "stretch",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mt: {
                                    xs: 0,
                                    "@media (max-height: 667px)": {
                                        mt: 0,
                                    },
                                    lg: 0,
                                },
                            }}
                        >
                            {currentStoryIndex > 0 && (
                                <IconButton
                                    aria-label="previous story"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handlePreviousStory();
                                    }}
                                    sx={{
                                        position: "absolute",
                                        left: isMobile ? "5px" : "100px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 2,
                                        color: PureLightThemeCaaS.colors.alpha.black[100],
                                        p: 0.25,
                                    }}
                                >
                                    <ArrowLeftIcon sx={{ fontSize: 120 }} />
                                </IconButton>
                            )}
                            <Box
                                component="img"
                                key={currentStoryIndex}
                                src={isTablet ? firstPageStories[currentStoryIndex].imgTablet : firstPageStories[currentStoryIndex].img} // If the screen is a tablet, use the tablet image, otherwise use the mobile image
                                alt={firstPageStories[currentStoryIndex].name}
                                className={showingNewStory ? styles.storyFadeIn : ""}
                                sx={{
                                    // Conditional width for tablet
                                    width: {
                                        xs: isLastStory ? "450px" : "400px",
                                        sm: isLastStory ? "600px" : "500px", // 600px for the last story on sm, 500px for others
                                        md: isLastStory ? "600px" : "500px", // 600px for the last story on md, 500px for others
                                        lg: "auto",
                                    },
                                    // if is mobile, height 375 and if is bigger 480px please
                                    //   height:  isMobile ? "500px" : "375px", 
                                    height: "300px",
                                    objectFit: "contain",
                                    // Only shrink on short *narrow* phones — not wide short screens (Nest Hub 1024x600)
                                    "@media (max-height: 667px) and (max-width: 600px)": {
                                        width: "400px",
                                        height: "300px",
                                    },
                                }}
                            />
                            {!isLastStory && (
                                <IconButton
                                    aria-label="next story"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleNextStory();
                                    }}
                                    sx={{
                                        position: "absolute",
                                        right: isMobile ? "5px" : "100px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 2,
                                        color: PureLightThemeCaaS.colors.alpha.black[100],
                                        p: 0.25,
                                    }}
                                >
                                    <ArrowRightIcon sx={{ fontSize: 120 }} />
                                </IconButton>
                            )}
                        </Box>
                    </Box>

                    {/* Box final for the logo (reserved space for the logo after the splash screen) and the button */}
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap={1}
                        sx={{
                            width: "100%",
                            pb: 0.5,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                mt: 2,
                                mb: 1, // Bottom margin before the button
                                pointerEvents: "none",
                            }}
                        >
                            <BlackLogo />
                            {/* <Logo /> */}
                        </Box>

                        <Box display="flex" flexDirection={isMobileOrTablet ? "column" : "row"} alignItems="center" gap={1} >
                            <Button
                                variant="contained"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    login(e);
                                }}
                                sx={{
                                    fontFamily: "ulp-font, -apple-system, BlinkMacSystemFont, Roboto, Helvetica, sans-serif",
                                    fontSize: "16px",
                                    fontWeight: "normal",
                                    borderRadius: 0.3,
                                    width: "250px",
                                    height: "40px",
                                    bgcolor: "#EDEDED",
                                    border: "1px solid #000000",
                                    color: "#000000",
                                    "&:hover": {
                                        bgcolor: "#EDEDED",
                                        color: "#000000",
                                    },
                                    "&:active": {
                                        bgcolor: "#EDEDED",
                                        color: "#000000",
                                    },
                                    "&:focus": {
                                        bgcolor: "#EDEDED",
                                        color: "#000000",
                                    },
                                    "&:focus-visible": {
                                        bgcolor: "#EDEDED",
                                        color: "#000000",
                                    },
                                }}
                            >
                                Log In
                                {/* Sign Up For Free */}
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    login(e);
                                }}
                                sx={{
                                    fontFamily: "ulp-font, -apple-system, BlinkMacSystemFont, Roboto, Helvetica, sans-serif",
                                    fontSize: "16px",
                                    fontWeight: "normal",
                                    borderRadius: 0.3,
                                    width: "250px",
                                    height: "40px",
                                    bgcolor: "#EDEDED",
                                    border: "1px solid #000000",
                                    color: "#000000",
                                    "&:hover": {
                                        bgcolor: "#EDEDED",
                                        color: "#000000",
                                    },
                                    "&:active": {
                                        bgcolor: "#EDEDED",
                                        color: "#000000",
                                    },
                                    "&:focus": {
                                        bgcolor: "#EDEDED",
                                        color: "#000000",
                                    },
                                    "&:focus-visible": {
                                        bgcolor: "#EDEDED",
                                        color: "#000000",
                                    },
                                }}
                            >
                                {/* Log In */}
                                Sign Up For Free
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default SecondGetStartedPage;
